import type { DestroyOptions, FederatedPointerEvent } from 'pixi.js';
import { Container } from 'pixi.js';
import { SettingsSliderLayout } from '../../../../../layouts/common/SettingsSliderLayout';
import MathUtils from '../../../../utils/MathUtils';

export interface SettingsSliderViewConfig {
  readonly minValue: number;
  readonly maxValue: number;
  readonly initialValue?: number;
  readonly onValueChanged?: (value: number) => void;
  readonly onValueFinalized?: (value: number) => void;
}

export class SettingsSliderView extends Container {
  private readonly layout = new SettingsSliderLayout();

  private value: number;

  private readonly knobGraphicRatio = 0.9;

  constructor(private readonly config: SettingsSliderViewConfig) {
    super();

    this.layout.root.props({
      parent: this,
    });

    this.value = config.initialValue ?? config.minValue;

    this.updateKnobPosition();
    this.setPointerEvents();
  }

  private setPointerEvents(): void {
    const {
      layout: { knob, background },
      config: { onValueFinalized },
      knobGraphicRatio,
    } = this;

    knob.interactive = true;
    knob.cursor = 'pointer';
    knob.onpointerdown = (): void => {
      const onKnobReleased = (): void => {
        knob.onpointerup = undefined;
        knob.onpointerupoutside = undefined;
        knob.onglobalpointermove = undefined;
        onValueFinalized?.(this.getValue());
      };

      const onPointerMove = (e: FederatedPointerEvent): void => {
        const localPos = e.getLocalPosition(this);
        const knobXOffset = (knob.width * knobGraphicRatio) / 2;
        const minX = knobXOffset;
        const maxX = background.width - knobXOffset;
        const ratio = MathUtils.clamp((localPos.x - minX) / (maxX - minX), 0, 1);
        this.setValueByRatio(ratio);
      };

      knob.onpointerup = onKnobReleased;
      knob.onpointerupoutside = onKnobReleased;
      knob.onglobalpointermove = onPointerMove;
    };
  }

  private updateKnobPosition(): void {
    const {
      layout: { knob, background },
      knobGraphicRatio,
    } = this;
    const knobXOffset = (knob.width * knobGraphicRatio) / 2;
    const minX = knobXOffset;
    const maxX = background.width - knobXOffset;
    knob.x = minX + this.getValueRatio() * (maxX - minX);
  }

  public getValueRatio(): number {
    const { minValue, maxValue } = this.config;
    return MathUtils.clamp(this.value / (maxValue - minValue), 0, 1);
  }

  public setValueByRatio(ratio: number): void {
    const { minValue, maxValue } = this.config;
    const value = minValue + MathUtils.clamp(ratio, 0, 1) * (maxValue - minValue);
    this.setValue(value);
  }

  public getValue(): number {
    return this.value;
  }

  public setValue(value: number): void {
    const {
      config: { minValue, maxValue, onValueChanged },
      value: previousValue,
    } = this;
    this.value = MathUtils.clamp(value, minValue, maxValue);
    this.updateKnobPosition();
    if (this.value !== previousValue) onValueChanged?.(this.value);
  }

  public destroy(options?: DestroyOptions): void {
    super.destroy(options);
  }
}
